import { useCallback, useState } from "react";

interface ThreadState {
  channelId: string | null;
  threadId: string | null;
}

/**
 * Owns the open-thread-panel state for `MessageArea`.
 *
 * Tracks `(channelId, threadId)` together so the panel auto-closes when
 * the user switches channels — the parent reads `activeThread` as
 * `threadState.threadId` ONLY when `threadState.channelId === channelId`.
 *
 * `setActiveThread(threadId)` toggles: passing the same id while a
 * thread is open closes it. The notification-deep-link routing path
 * needs to bypass that toggle (clicking a notification on an already-
 * open thread should keep it open and just re-scroll), so it calls
 * `setThreadState` directly instead.
 */
export function useThreadState(channelId: string | null) {
  const [threadState, setThreadState] = useState<ThreadState>({
    channelId: null,
    threadId: null,
  });

  const activeThread =
    threadState.channelId === channelId ? threadState.threadId : null;

  const setActiveThread = useCallback(
    (threadId: string | null) => {
      setThreadState((prev) =>
        prev.channelId === channelId && prev.threadId === threadId
          ? { channelId, threadId: null }
          : { channelId, threadId },
      );
    },
    [channelId],
  );

  return { threadState, setThreadState, activeThread, setActiveThread };
}
